import * as React from 'react'

import {
  TagColor as Color,
  StatusLabel,
  StatusLabelTypes,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import type { EsignatureSignature } from 'modules/esignatures/hooks/signatures/useGetEsignatureSignatures'
import { SignatureStatuses } from 'modules/esignatures/lib/signatures/signatureStatuses'

interface Props {
  signature: EsignatureSignature
}

const StatusTooltip = ({ signature }: Props) => {
  const [open, toggle] = useToggle(false)

  const status = signature.status as unknown as SignatureStatuses

  const statusToTagColor: Record<SignatureStatuses, Color> = {
    [SignatureStatuses.Created]: Color.champagne,
    [SignatureStatuses.Signed]: Color.viridian,
    [SignatureStatuses.Declined]: Color.radical,
    [SignatureStatuses.Bounced]: Color.radical,
    [SignatureStatuses.Cancelled]: Color.grey,
  }

  const statusToStatusLabelType: Record<SignatureStatuses, StatusLabelTypes> = {
    [SignatureStatuses.Created]: 'warning',
    [SignatureStatuses.Signed]: 'positive',
    [SignatureStatuses.Declined]: 'critical',
    [SignatureStatuses.Bounced]: 'critical',
    [SignatureStatuses.Cancelled]: 'neutral',
  }

  const statusLabel: Record<SignatureStatuses, string> = {
    [SignatureStatuses.Created]: i18n.t('documents.modal.esignature.status.created'),
    [SignatureStatuses.Signed]: i18n.t('documents.modal.esignature.status.signed'),
    [SignatureStatuses.Declined]: i18n.t('documents.modal.esignature.status.declined'),
    [SignatureStatuses.Bounced]: i18n.t('documents.modal.esignature.status.bounced'),
    [SignatureStatuses.Cancelled]: i18n.t('documents.modal.esignature.status.cancelled'),
  }

  const tooltipLabel = (signature: EsignatureSignature) => {
    switch (signature.status) {
      case SignatureStatuses.Created:
        return i18n.t('documents.modal.esignature.document_created', {
          employee: signature.signee.fullName,
        })
      case SignatureStatuses.Signed:
        return i18n.t('documents.modal.esignature.document_signed', {
          employee: signature.signee.fullName,
        })
      case SignatureStatuses.Declined:
        return i18n.t('documents.modal.esignature.document_declined', {
          employee: signature.signee.fullName,
        })
      case SignatureStatuses.Bounced:
        return i18n.t('documents.modal.esignature.document_bounced', {
          employee: signature.signee.fullName,
        })
      case SignatureStatuses.Cancelled:
        return i18n.t('documents.modal.esignature.document_cancelled')
      default:
        return i18n.t('documents.modal.esignature.status.created')
    }
  }

  return (
    <Tooltip.Dialog
      attachment="bottom right"
      targetAttachment="top right"
      open={open}
      toggle={toggle}
      label={tooltipLabel(signature)}
    >
      <StatusLabel ellipsis type={statusToStatusLabelType[status]}>
        {statusLabel[status]}
      </StatusLabel>
    </Tooltip.Dialog>
  )
}

export default StatusTooltip
